<template>
  <div ref="containerRef" class="my-tokens">
    <h1 v-if="title" class="title">{{ title }}</h1>

    <div class="flexContainer">
      <CustomDropdown
        v-if="$device.isMobile"
        :options="options"
        :default-value="selected"
        @handle-change="dropdownChangeHandler"
      >
        <template #listItem="slotProps">
          <h3 class="tokenName">
            <img v-if="isTokenOption(slotProps.option)" :src="slotProps.option.src" alt="" />
            <img v-else-if="isCustomButton(slotProps.option)" :src="slotProps.option.src" alt="" />

            <span v-if="isTokenOption(slotProps.option)">
              {{ slotProps.option.label }}
            </span>
            <span v-else-if="isCustomButton(slotProps.option)">
              {{ $t(slotProps.option.sectionLabel) }}
            </span>

            <UIChevron v-if="isTokenOption(slotProps.option) && slotProps.option.apr" :type="ChevronType.Apr">
              <span class="chevron-apr-label">{{ $t('appStatisticAPR') }}:</span>
              <span class="chevron-apr-value">
                {{ Formatters.number.renderString((f) => f.formatPercent((slotProps.option as any).apr)) }}%
              </span>
            </UIChevron>
          </h3>
        </template>
      </CustomDropdown>
      <div v-else class="tokensContainer">
        <el-skeleton :loading animated style="width: 100%; height: 100%">
          <template #template>
            <div class="tw:flex tw:justify-center tw:gap-2">
              <el-skeleton-item v-for="i in 8" :key="i" style="width: 95px; height: 95px" />
            </div>
          </template>
          <template #default>
            <OptionButton
              v-for="option in options"
              :key="option.key"
              :selected="selected === option.key"
              :option="option"
              @click="() => tokenClickHandler(option.key as Tokens)"
            />
          </template>
        </el-skeleton>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { Tokens } from '~/utils/constants';
import { ChevronType } from '~/utils/constants/ui';
import { type CustomButton, type ITokenOptionItem, MarketplaceSections } from '~/types/marketplace';
import { isCustomButton, isTokenOption } from '~/types/typeguards';
import { Formatters } from '~/utils/formatters/formatters';

const route = useRoute();
const store = useMainStore();

const { loading = false, selected } = defineProps<{
  options: Array<ITokenOptionItem | CustomButton>;
  selected: Tokens | MarketplaceSections;
  title?: string;
  loading?: boolean;
}>();

const emit = defineEmits(['set-active-tab']);

const containerRef = ref<HTMLDivElement>();

const dropdownChangeHandler = (checkedValue: ITokenOptionItem) => {
  emit('set-active-tab', checkedValue.key);
};

// TODO: refactor
watchEffect((onCleanup) => {
  let timeout: ReturnType<typeof setTimeout>;

  if (route.query.scrollToContainer && containerRef.value) {
    emit('set-active-tab', route.query.token);
    store.setCurrentToken(route.query.token as Tokens);

    timeout = setTimeout(() => {
      containerRef.value?.scrollIntoView({ behavior: 'smooth' });
    }, 500);

    navigateTo({ query: { ...route.query, scrollToContainer: undefined, token: undefined } });
    return;
  }

  onCleanup(() => {
    clearTimeout(timeout);
  });
});

const tokenClickHandler = (buttonKey: Tokens | MarketplaceSections) => {
  if (buttonKey === selected) {
    return;
  }

  emit('set-active-tab', buttonKey);
};
</script>

<style scoped lang="scss">
.my-tokens {
  margin-bottom: 25px;
  border: none;
  background: inherit;
  width: 100%;
  max-width: 100%;
}

.title {
  color: #fff;
  width: 100%;
  text-align: center;
  font-size: 64px;
  font-weight: 900;
  line-height: 94.72px;
  margin: 0;

  @media screen and (max-width: 990px) {
    font-size: 48px;
  }
}

.tokensContainer {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  overflow: visible;
  padding: 12px;
  width: 100%;
}

.flexContainer {
  display: flex;

  .dropdown {
    background: linear-gradient(180deg, #0083ad 0%, #00354f 100%);
  }
}

.tokenName {
  img {
    height: 45px;
    width: 45px;
  }

  & > span {
    font-size: 20px;
  }

  color: #fff;
  display: flex;
  align-items: center;
  gap: 10px;
  white-space: nowrap;
}

@media screen and (max-width: 768px) {
  .tokensContainer {
    gap: 20px;
    padding: 8px;
  }

  .tokenName {
    img {
      height: 35px;
      width: 35px;
    }
  }
}

@media screen and (max-width: 480px) {
  .tokensContainer {
    gap: 20px;
    padding: 6px;
  }

  .tokenName {
    img {
      height: 45px;
      width: 45px;
    }
  }
}
</style>
